import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import endPoints from "../routes/NetworkRoutes";

const PendingTasksContext = createContext({
  counts: {},
  totalPending: 0,
  loading: false,
  refreshPendingTasks: () => {},
  getCountForItem: () => 0,
  getCountForCollapse: () => 0,
});

export const PendingTasksProvider = ({ children }) => {
  const [counts, setCounts] = useState({
    studentLeaves: 0,
    staffLeaves: 0,
    grievances: 0,
    studentVerification: 0,
    profileVerification: 0,
    discipline: 0,
    cancelAdmission: 0,
  });
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPendingCounts = useCallback(async () => {
    try {
      const userData = JSON.parse(window.localStorage.getItem("User State") || "{}");
      const token = userData?.token;
      if (!token) return;

      const response = await axios.get(endPoints.getPendingTasksCounts, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.status === 1 && response?.data?.data) {
        const data = response.data.data;
        setCounts(data.counts || {});
        setTotalPending(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching pending task counts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingCounts();

    // Auto-refresh pending counts every 40 seconds
    const interval = setInterval(() => {
      fetchPendingCounts();
    }, 40000);

    return () => clearInterval(interval);
  }, [fetchPendingCounts]);

  // Map individual NavItem by id or URL
  const getCountForItem = useCallback(
    (item) => {
      if (!item) return 0;
      const id = (item.id || "").toLowerCase();
      const url = (item.url || "").toLowerCase();

      // Parse user role to distinguish student vs staff/admin
      let isStudent = false;
      try {
        const userData = JSON.parse(window.localStorage.getItem("User State") || "{}");
        const role = parseInt(userData?.user?.role || userData?.role || 0);
        isStudent = role === 4;
      } catch {
        isStudent = url.includes("/userdashboard");
      }

      // Student Verification
      if (
        id === "unverified students" ||
        url.includes("/verifystudent/unverifiedstudents")
      ) {
        return counts.studentVerification || 0;
      }

      // Profile Verification
      if (
        id === "profilemanagement" ||
        id === "studentprofilemanagement" ||
        id === "manageprofile" ||
        id === "profile" ||
        url.includes("/studentprofilemanagement") ||
        url.includes("/manageprofile")
      ) {
        return counts.profileVerification || 0;
      }

      // Staff Leaves (checked before student leaves to avoid URL substring collision)
      if (
        id === "manageleaverequest" ||
        id === "staffleave" ||
        url.includes("/staffleavemanagement")
      ) {
        return counts.staffLeaves || 0;
      }

      // Student Leaves
      if (
        id === "leaves" ||
        id === "leave" ||
        id === "manage leave" ||
        id === "manageleave" ||
        url.includes("/leavemanagement/manageleave") ||
        url.includes("/manageleave/manageleave") ||
        (url.includes("/leavemanagement") && !url.includes("/staffleavemanagement"))
      ) {
        return counts.studentLeaves || 0;
      }

      // Grievances
      if (isStudent) {
        if (
          id === "myraisedgrivance" ||
          url.includes("/grivance/myraisedgrivance") ||
          url.includes("/grievance/myraisedgrievance")
        ) {
          return counts.grievances || 0;
        }
      } else {
        if (
          id === "allgrievancerequest" ||
          id === "grievancemanagement" ||
          url.includes("/grievancemanagementadmin/allgrievancerequest") ||
          url.includes("/grievancemanagement/allgrievancerequest")
        ) {
          return counts.grievances || 0;
        }
      }

      // Discipline
      if (isStudent) {
        if (
          id === "mydisciplinaryaction" ||
          url.includes("/disciplinaryaction/mydisciplinaryactions")
        ) {
          return counts.discipline || 0;
        }
      } else {
        if (
          id === "discipline" ||
          id === "alldisciplinaryaction" ||
          url.includes("/disciplinaryaction/alldisciplinaryaction")
        ) {
          return counts.discipline || 0;
        }
      }

      // Cancel Admission
      if (
        id === "allcanceladmissionrequest" ||
        url.includes("/canceladmission/allcanceladmissionrequest")
      ) {
        return counts.cancelAdmission || 0;
      }

      return 0;
    },
    [counts]
  );

  // Map NavCollapse menu by summing children or category id
  const getCountForCollapse = useCallback(
    (collapseMenu) => {
      if (!collapseMenu) return 0;
      const id = (collapseMenu.id || "").toLowerCase();

      // Direct category checks
      if (id === "verifystudent") return counts.studentVerification || 0;
      if (id === "leave management" || id === "leave") return counts.studentLeaves || 0;
      if (id === "staffleavemanagement") return counts.staffLeaves || 0;
      if (id === "grievancemanagement" || id === "grivance") return counts.grievances || 0;
      if (id === "disciplinaryaction") return counts.discipline || 0;
      if (id === "canceladmission") return counts.cancelAdmission || 0;

      // Student Management cumulative sum
      if (id === "studentmanagement") {
        if (collapseMenu.children && Array.isArray(collapseMenu.children)) {
          const calculated = collapseMenu.children.reduce((acc, child) => {
            if (child.type === "item") {
              return acc + getCountForItem(child);
            }
            if (child.type === "collapse") {
              return acc + getCountForCollapse(child);
            }
            return acc;
          }, 0);
          if (calculated > 0) return calculated;
        }
        return (
          (counts.studentLeaves || 0) +
          (counts.profileVerification || 0) +
          (counts.discipline || 0) +
          (counts.cancelAdmission || 0)
        );
      }

      // Recursive sum of children if not matched by category ID
      if (collapseMenu.children && Array.isArray(collapseMenu.children)) {
        return collapseMenu.children.reduce((acc, child) => {
          if (child.type === "item") {
            return acc + getCountForItem(child);
          }
          if (child.type === "collapse") {
            return acc + getCountForCollapse(child);
          }
          return acc;
        }, 0);
      }

      return 0;
    },
    [counts, getCountForItem]
  );

  return (
    <PendingTasksContext.Provider
      value={{
        counts,
        totalPending,
        loading,
        refreshPendingTasks: fetchPendingCounts,
        getCountForItem,
        getCountForCollapse,
      }}
    >
      {children}
    </PendingTasksContext.Provider>
  );
};

export const usePendingTasks = () => useContext(PendingTasksContext);
export default PendingTasksContext;
